import { useState } from "react";
import {
  Descriptions,
  Tag,
  Button,
  Divider,
  Timeline,
  Empty,
  Card,
} from "antd";
import {
  ArrowRightOutlined,
  DownloadOutlined,
  FileTextOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import trans, { formatDateTime, isRTL } from "../../../utils";
import { formatDateString } from "../../../JsHelpers/DateFormat";
import { Modal } from "react-bootstrap";
import Popup from "../../Helpers/Popup/Popup";
import PopupTitle from "../../Helpers/Popup/PopupParts/PopupTitle";
import Close from "../../Helpers/CloseBtn/Close";
import InspectionRequestFormSubmission from "./InspectionRequestFormSubmission";
import { useGetSpecificFormQuery } from "../../../Redux/service/Forms/Form";
import useIsMobile from "../../../hooks/useIsMobile";

const InspectionRequestRevisions = ({
  data,
  currentRevisionIndex,
  allData,
}) => {
  const isMobile = useIsMobile();
  const [showFormPopup, setShowFormPopup] = useState(false);
  const currentIndex =
    currentRevisionIndex !== undefined
      ? currentRevisionIndex
      : data?.length > 0
        ? data?.length - 1
        : 0;
  const currentRevision = data?.[currentIndex];
  const snapshot = currentRevision?.form_data_snapshot || [];

  // Get form fields for the popup
  const { data: formFields } = useGetSpecificFormQuery(allData?.form?.id, {
    skip: !allData?.form?.id || !showFormPopup,
    refetchOnMountOrArgChange: true,
  });

  // Convert snapshot to form data format expected by InspectionRequestFormSubmission
  const convertSnapshotToFormData = () => {
    if (!snapshot || !formFields?.data?.formSchema?.fields) return [];

    const fields = formFields.data.formSchema.fields;
    const flattenFields = (fields) => {
      return fields.flatMap((field) => {
        if (field.name === "Container" && Array.isArray(field.container)) {
          return [field, ...flattenFields(field.container)];
        }
        return field;
      });
    };

    const allFields = flattenFields(fields);

    console.log("📋 Form Schema Fields:", allFields);
    console.log("📸 Snapshot Data:", snapshot);

    return allFields.map((field) => {
      // Only match by key (unique identifier)
      const snapshotItem = snapshot.find((item) => item.key === field.key);

      const result = {
        key: field.key,
        value: snapshotItem?.value || field.defaultValue || null,
      };

      // Log matching results for debugging
      if (snapshotItem) {
        console.log(
          `✅ Matched field "${field.key}" with snapshot value:`,
          snapshotItem.value,
        );
      } else {
        console.log(
          `⚠️ No snapshot match for field "${field.key}" (${field.name})`,
        );
      }

      return result;
    });
  };

  const formDataValues = showFormPopup ? convertSnapshotToFormData() : [];

  const statusColors = {
    CLOSED: `blue`,
    PENDING_QA: `orange`,
    PENDING_CONSULTANT: `orange`,
    QA_APPROVED: `green`,
    CONSULTANT_APPROVED: `green`,
    APPROVED: `green`,
    REJECTED: `red`,
    QA_REJECTED: `red`,
    CONSULTANT_REJECTED: `red`,
    CONSULTANT_APPROVED_WITH_COMMENTS: `purple`,
    DRAFT: `default`,
    CANCELLED: `default`,
  };

  const timelineColor = (severity) => {
    switch (severity) {
      case "MINOR":
        return "green"; // or lime-like closest
      case "MAJOR":
        return "orange"; // closest to #ffe58f
      case "CRITICAL":
        return "red"; // closest to #ffa39e
      default:
        return "gray";
    }
  };

  const borderColor = (severity) => {
    if (severity === "MINOR") {
      return "lime";
    } else if (severity === "MAJOR") {
      return "#ffe58f";
    } else if (severity === "CRITICAL") {
      return "#ffa39e";
    } else {
      return "#fff3f39f";
    }
  };

  const getFileIconUrl = (type = "") => {
    const lowerType = type.toLowerCase();
    if (lowerType === "image/png") {
      return `${process.env.PUBLIC_URL}/assets/PNG.svg`;
    } else if (lowerType === "image/jpg" || lowerType === "image/jpeg") {
      return `${process.env.PUBLIC_URL}/assets/JPG.svg`;
    } else if (lowerType === "application/pdf") {
      return `${process.env.PUBLIC_URL}/assets/PDF.svg`;
    } else {
      return `${process.env.PUBLIC_URL}/assets/Excel.svg`;
    }
  };

  const downloadAllAttachments = async () => {
    if (
      !currentRevision?.attachments ||
      currentRevision?.attachments.length === 0
    )
      return;

    // Download each file with a small delay to avoid browser blocking
    for (let i = 0; i < currentRevision?.attachments.length; i++) {
      const file = currentRevision?.attachments[i];
      setTimeout(() => {
        const link = document.createElement("a");
        link.href = file?.url;
        link.download =
          file?.originalName ||
          file?.name ||
          file?.filename ||
          `attachment-${i + 1}`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, i * 200);
    }
  };

  const downloadFormPDF = () => {
    if (
      !allData?.id ||
      currentRevision?.revision_number === null ||
      !currentRevision?.revision_type
    ) {
      return;
    }

    const baseUrl = process.env.REACT_APP_BACKEND2_URL || "";
    const url = `${baseUrl}/v1/inspection-requests/${allData.id}/hdb_pdf?revisionNumber=${currentRevision.revision_number}&revisionType=${currentRevision.revision_type}`;

    // Fetch with authorization header
    fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to download PDF");
        }
        return response.blob();
      })
      .then((blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${allData?.ir_number || allData?.id}-Rev-${
          currentRevision.revision_number
        }.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((error) => {
        console.error("Error downloading PDF:", error);
        // Fallback: try opening in new tab
        window.open(url, "_blank");
      });
  };

  const downloadExcel = () => {
    if (!allData?.ir_number || !currentRevision?.revision_type) {
      return;
    }

    const revisionType = currentRevision.revision_type.toLowerCase(); // iir or eir
    const irNumber = allData.ir_number;
    const lang = isRTL ? "ar" : "en";
    const baseUrl = "https://dev.softsprint.ae/backendTest";
    const url = `${baseUrl}/ir/export/${revisionType}-revisions/excel?search=${irNumber}&lang=${lang}`;

    // Fetch with authorization header
    fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to download Excel");
        }
        return response.blob();
      })
      .then((blob) => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `${irNumber}-${revisionType.toUpperCase()}-revisions.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      })
      .catch((error) => {
        console.error("Error downloading Excel:", error);
        // Fallback: try opening in new tab
        window.open(url, "_blank");
      });
  };

  const items = [
    {
      label: trans("Revision"),
      children: <Tag color="green">Rev-{currentRevision?.revision_number}</Tag>,
      span: 4,
    },
    {
      label: trans("Status"),
      span: 4,
      children: (
        <>
          <Tag color={statusColors[currentRevision?.status]}>
            {currentRevision?.status}
          </Tag>
          {currentRevision?.review_result && (
            <>
              <ArrowRightOutlined />{" "}
              <Tag color={statusColors[currentRevision?.review_result]}>
                {currentRevision?.review_result}
              </Tag>
            </>
          )}
        </>
      ),
    },
    {
      label: trans("Submitted At"),
      children: currentRevision?.submitted_at
        ? formatDateTime(currentRevision?.submitted_at)
        : "In Progress",
      span: 2,
    },
    {
      label: trans("Reviewd At"),
      span: 2,
      children: currentRevision?.reviewed_at
        ? formatDateTime(currentRevision?.reviewed_at)
        : "In Progress",
    },
    {
      label: trans("Submitted By"),
      span: 2,
      children: currentRevision?.submittedBy?.name,
    },
    {
      label: trans("Reviewed By"),
      span: 2,
      children: currentRevision?.reviewedBy?.name,
    },
    {
      label: trans("Comment"),
      span: 4,
      children: currentRevision?.review_comments,
    },
  ];

  const snapshotItems = snapshot.map((item) => ({
    label: item.title || item.name,
    children: item.value || "No Value",
    span: 4,
  }));

  //   const items2 = [
  //     {
  //       label: trans("Start Date"),
  //       span: 2,
  //       children: formatDateTime(data?.startTime),
  //     },
  //     {
  //       label: trans("Expected Duration"),
  //       span: 2,
  //       children: formatDuration(data?.expectedDuration),
  //     },
  //     {
  //       label: trans("Expected Completion"),
  //       span: 2,
  //       children: data?.completionTime
  //         ? formatDateTime(data?.completionTime)
  //         : "No Data",
  //     },
  //   ];

  return (
    <>
      {data?.length > 0 ? (
        <>
          <Descriptions
            title={
              <div className="d-flex justify-content-between align-items-center">
                <div className="fs-16 fw-700 brand-700">
                  {trans("Revision Information")} ({trans("Revision")}{" "}
                  {currentIndex + 1} {trans("of")} {data?.length || 0})
                </div>
                {!isMobile && (
                  <div className="d-flex gap-2">
                    <Button
                      type="default"
                      icon={<DownloadOutlined />}
                      onClick={downloadFormPDF}
                      disabled={
                        currentRevision?.revision_number == null ||
                        !currentRevision?.revision_type
                      }
                    >
                      {trans("Download Form")}
                    </Button>
                    <Button
                      type="default"
                      icon={<FileExcelOutlined />}
                      onClick={downloadExcel}
                      disabled={
                        !allData?.ir_number || !currentRevision?.revision_type
                      }
                    >
                      {trans(
                        `Download ${currentRevision?.revision_type.toUpperCase()}-log`,
                      )}
                    </Button>
                  </div>
                )}
              </div>
            }
            size={"small"}
            bordered
            items={items.map((item) => ({
              ...item,
              labelStyle: { width: "200px" }, // adjust here
            }))}
            style={{ marginBottom: 24 }}
          />
          {snapshotItems.length > 0 && (
            <>
              <Divider />
              <div
                className={`d-flex justify-content-between align-items-center mb-3 ${isMobile ? "flex-column" : ""}`}
              >
                <div className="fs-16 fw-700 brand-700">
                  {trans("Form Submission")}
                </div>
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={() => setShowFormPopup(true)}
                >
                  {trans("View Form Submission")}
                </Button>
              </div>
            </>
          )}
          {currentRevision?.correction_notes &&
            currentRevision?.revision_type === "EIR" && (
              <>
                <Divider />
                <div
                  className="fs-16 fw-700 brand-700"
                  style={{ marginBottom: 10 }}
                >
                  {trans("QA / QC Corrections")}
                </div>
                <Descriptions
                  size="small"
                  bordered
                  items={[
                    {
                      label: trans("Correction Notes"),
                      children: currentRevision?.correction_notes || "-",
                      span: 4,
                    },
                    {
                      label: trans("Ready Date"),
                      children: currentRevision?.ready_date
                        ? currentRevision.ready_date.split("T")[0]
                        : "-",
                      span: 2,
                    },
                  ].map((item) => ({
                    ...item,
                    labelStyle: { width: "200px" },
                  }))}
                  style={{ marginBottom: 24 }}
                />
                {currentRevision?.attachments?.length > 0 && (
                  <Card className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <FileTextOutlined style={{ color: "green" }} />
                        <h5 className="mb-0">{trans("Attachments")}</h5>
                      </div>
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadAllAttachments}
                        size="middle"
                      >
                        {trans("Download All")}
                      </Button>
                    </div>
                    <div className="d-flex flex-column gap-3">
                      {currentRevision.attachments.map((file, index) => (
                        <div
                          key={index}
                          className="d-flex justify-content-between align-items-center border p-3 rounded pointer"
                          onClick={() => window.open(`${file?.url}`, "_blank")}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <img
                              src={getFileIconUrl(file?.mimetype)}
                              alt={file?.mimeType || file?.type || "file"}
                              width={28}
                            />
                            <div>
                              {file?.filename ||
                                `${trans("Attachments")} ${index + 1}`}
                              <br />
                              {file?.size && (
                                <div className="text-muted small">
                                  {file.size} MB
                                </div>
                              )}
                              {file?.uploadedBy && (
                                <div className="text-muted small">
                                  {trans("Uploaded by")}{" "}
                                  <span className="fw-800">
                                    {file.uploadedBy?.name}
                                  </span>{" "}
                                  {trans("on")}{" "}
                                  <span className="fw-800">
                                    {formatDateString(file.uploaded_at)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <DownloadOutlined
                            className="text-secondary pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              const link = document.createElement("a");
                              link.href = `${file?.url}`;
                              link.download =
                                file?.originalName ||
                                file?.name ||
                                file?.filename ||
                                `attachment-${index + 1}`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </>
            )}
          {currentRevision?.deficiencies?.length > 0 && (
            <>
              <Divider />
              <div
                className="fs-16 fw-700 brand-700"
                style={{ marginBottom: 10 }}
              >
                {trans("Deficiencies")}
              </div>

              {currentRevision?.deficiencies?.map((i) => (
                <>
                  <Timeline
                    key={i?.id}
                    items={[
                      {
                        key: i?.id,
                        dot: (
                          <div
                            style={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              background: borderColor(i?.severity),
                            }}
                          />
                        ),
                        color: timelineColor(i?.severity),
                        children: (
                          <div className="d-flex flex-column gap-3">
                            {/* ========== Identified Section ========== */}
                            <div>
                              <div className="fw-bold fs-6">
                                {trans("Identified Details")}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Deficiency Severity")}:
                                </span>{" "}
                                {trans(i?.severity)}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Deficiency Category")}:
                                </span>{" "}
                                {trans(i?.deficiency_category)}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Description")}:
                                </span>{" "}
                                {trans(i?.description)}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Date Identified")}:
                                </span>{" "}
                                {formatDateString(i?.identified_at)}
                              </div>

                              <div className="text-secondary small mt-1">
                                <span className="fw-semibold">
                                  {trans("Identified By")}:
                                </span>{" "}
                                {i?.identifiedByUser?.name || "-"}
                              </div>
                            </div>

                            {/* ========== Correction Section ========== */}
                            {i?.corrected && (
                              <div>
                                <div className="fw-bold fs-6">
                                  {trans("Correction Details")}
                                </div>

                                <div className="text-secondary small mt-1">
                                  <span className="fw-semibold">
                                    {trans("Notes")}:
                                  </span>{" "}
                                  {trans(i?.correction_notes)}
                                </div>

                                <div className="text-secondary small mt-1">
                                  <span className="fw-semibold">
                                    {trans("Corrected At")}:
                                  </span>{" "}
                                  {formatDateString(i?.corrected_at)}
                                </div>

                                <div className="text-secondary small mt-1">
                                  <span className="fw-semibold">
                                    {trans("Corrected By")}:
                                  </span>{" "}
                                  {i?.correctedByUser?.name || "-"}
                                </div>
                              </div>
                            )}
                          </div>
                        ),
                      },
                    ]}
                  />
                </>
              ))}
            </>
          )}
          {/* {lastRevision?.attachments?.length > 0 && (
        <div className="col-lg-12 mt-3">
          <Card className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0">{trans("Attachments")}</h5>
              </div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={downloadAllAttachments}
                size="middle"
              >
                {trans("Download All")}
              </Button>
            </div>
            <div className="d-flex flex-column gap-3">
              {lastRevision?.attachments.map((file, index) => (
                <div
                  key={index}
                  className="d-flex justify-content-between align-items-center border p-3 rounded pointer"
                  onClick={() => window.open(`${file?.url}`, "_blank")}
                >
                  <div className="d-flex align-items-center gap-3">
                    <img
                      src={getFileIconUrl(file?.mimetype)}
                      alt={file?.mimeType || file?.type || "file"}
                      width={28}
                    />
                    <div>
                      {file?.filename || `${trans("Attachments")} ${index + 1}`}
                      <br />
                      {file?.size && (
                        <div className="text-muted small">{file.size} MB</div>
                      )}
                      {file?.uploadedBy && (
                        <div className="text-muted small">
                          {trans("Uploaded by")}{" "}
                          <span className="fw-800">
                            {file.uploadedBy?.name}
                          </span>{" "}
                          {trans("on")}{" "}
                          <span className="fw-800">
                            {formatDateString(file.uploaded_at)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <DownloadOutlined
                    className="text-secondary pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      const link = document.createElement("a");
                      link.href = `${file?.url}`;
                      link.download =
                        file?.originalName ||
                        file?.name ||
                        file?.filename ||
                        `attachment-${index + 1}`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )} */}
        </>
      ) : (
        <div className="text-center">
          <Empty description={trans("No revisions found")} />
        </div>
      )}
      <Popup show={showFormPopup} size="lg">
        <Modal.Header className="modal-header flex-between">
          <Modal.Title id="contained-modal-title-vcenter">
            <PopupTitle
              headtitle={trans("Form Submission")}
              subtitle={
                <Tag color="green">
                  {trans("Revision")} {currentRevision?.revision_number}
                </Tag>
              }
            />
          </Modal.Title>
          <Close
            style="close-32 close"
            close={() => setShowFormPopup(false)}
            img="Close"
          />
        </Modal.Header>
        <Modal.Body className="modal-body-scrollable fixed-popup-height">
          {formFields?.data?.formSchema?.fields ? (
            <InspectionRequestFormSubmission
              values={formDataValues}
              formFields={formFields?.data?.formSchema?.fields}
              resubmit={false}
              setResubmit={() => {}}
              setActiveOption={() => {}}
              irId={allData?.id}
              refetch={() => {}}
              formRefetch={() => {}}
            />
          ) : (
            <div className="text-center py-4">
              <Empty description={trans("Loading form...")} />
            </div>
          )}
        </Modal.Body>
      </Popup>
    </>
  );
};

export default InspectionRequestRevisions;
